/**
 * Pure helpers for showing an attachment's translation instead of the original (CR-303134).
 *
 * A finished translation is an AttachFile record (attachBy = translation) that the list / detail
 * APIs already return in `attachFiles` next to the original's record. Its fileInfo carries
 * `sourceFileUrl` (the original's key) and `lang`.
 */

import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { normalizeLangCode as normalizeJobLangCode } from '@/common/utils/translationJobUtils';
import { normalizeLangCode as normalizeUserLangCode } from '@/common/utils/translationUtils';

const readFileInfo = record => Utils.convertJSONDataToObject(record?.fileInfo) || {};

/**
 * The viewer's main language as a translation language code, or null when the user has not set one.
 * Unlike resolveMainLanguage, an unset value does NOT fall back to Japanese: the spec keeps the
 * original for a user without a main language (CR-303134 §7).
 */
export const getUserMainLanguageCode = userDetail => {
    const userSetting = Utils.convertJSONDataToObject(userDetail?.userSetting) || {};
    const raw = typeof userSetting.mainLanguage === 'string' ? userSetting.mainLanguage.trim() : '';
    // Checked first: normalizeUserLangCode turns a blank code into the source language.
    if (!raw) return null;

    // legacy 'jp' -> 'ja', then the job-side aliases (he/iw, tl/fil)
    return normalizeJobLangCode(normalizeUserLangCode(raw));
};

/**
 * The translation record of one original file in one language, or null. Languages match exactly
 * after alias normalisation (he/iw, tl/fil) - regional variants never match (BR-2).
 *
 * @param {Array} attachFiles   AttachFile records of the ticket (parsed)
 * @param {string} sourceFileUrl key of the original file (with or without public/)
 * @param {string} lang         target language
 */
export const findTranslationAttachFile = (attachFiles, sourceFileUrl, lang) => {
    if (!Array.isArray(attachFiles) || !sourceFileUrl || !lang) return null;

    const sourceKey = Utils.removePrefixForS3Key(sourceFileUrl);
    const targetLang = normalizeJobLangCode(lang);

    return (
        attachFiles.find(record => {
            if (record?.attachBy !== Constant.ATTACH_FILE_TYPE.TRANSLATION) return false;
            const fileInfo = readFileInfo(record);
            return Utils.removePrefixForS3Key(fileInfo.sourceFileUrl) === sourceKey && normalizeJobLangCode(fileInfo.lang) === targetLang;
        }) || null
    );
};

/**
 * The file object to render/open in place of the original: same shape the attachment components
 * already use (fileUrl / fileName / fileSize), pointing at the translated file. A translated
 * document never has a thumbnail (BR-5a), so the original's thumbnail info is dropped.
 */
export const toTranslatedDisplayFile = (file, translationRecord) => {
    if (!translationRecord) return file;

    const fileInfo = readFileInfo(translationRecord);
    const { thumbnailInfo, ...rest } = file || {};

    return {
        ...rest,
        fileUrl: fileInfo.fileUrl || translationRecord.s3Path,
        fileName: fileInfo.fileName || rest.fileName,
        fileSize: fileInfo.fileSize ?? rest.fileSize,
        isTranslatedDisplay: true,
        translationAttachFile: translationRecord
    };
};

const translatedAttachmentUtils = {
    getUserMainLanguageCode,
    findTranslationAttachFile,
    toTranslatedDisplayFile
};

export default translatedAttachmentUtils;
